import * as React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import favicon from "../../../images/favicon1.png";
import { useVirtualPageView } from "../../../hooks/useVirtualPageView";
import { addKoddiPageTag } from "../../../utils/KoddiTagsGTM";
import { ISEOProps, ISEODataProps } from "./ISEOProps";

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        title
        description
      }
    }
  }
`;

const SEO: React.FC<ISEOProps> = (props) => {
  const data: ISEODataProps = useStaticQuery(detailsQuery);

  const title = props.title || data.site.siteMetadata.title;
  const description = props.description || data.site.siteMetadata.description;
  const keywords = props.keywords || [];
  const meta = props.meta || [];
  const lang = props.lang || "en";
  const includeLeaflet = props.includeLeaflet || false;

  useVirtualPageView(
    !props.isCheckoutPage ? title : props.checkoutPageTitle || title,
    props.setDL,
    props.crsCode,
    props.city,
    props.brand,
    props.brandPage
  );

  React.useEffect(() => {
    (async () => {
      if (props.koddiTitle) {
        await addKoddiPageTag(props.location, props.koddiTitle, props.crsCode);
      }
    })().catch(console.error);
  }, []);

  return (
    <Helmet
      async
      htmlAttributes={{ lang }}
      title={title}
      titleTemplate={title}
      meta={[
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
      ]
        .concat(
          keywords.length
            ? { name: "keywords", content: keywords.join(", ") }
            : []
        )
        .concat(meta)}
    >
      <link rel="icon" href={favicon} />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      {includeLeaflet && (
        <link
          rel="stylesheet"
          as="style"
          href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/leaflet.min.css"
        />
      )}
      {props.canonicalUrl && <link rel="canonical" href={props.canonicalUrl} />}
    </Helmet>
  );
};

/* Defaults + propTypes */
SEO.defaultProps = {
  lang: "en",
  meta: [],
  keywords: [],
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string.isRequired,
};

export default SEO;
